/** The six-verb enum, narrowed to what one added activity may do right now, one row, one layout. */

import { Button, StatusBadge, Toolbar } from "@/components/ui";

import { runStatusOf, verbsFor, type ActivityVerb } from "../lib/runState";
import { useActivityVerb } from "../spaces/lib/queries";
import type { AddedActivityRow } from "../types";

/** Optimistic feedback shows receipt, never outcome: "Stopping", never "Stopped". */
const RECEIPT: Record<ActivityVerb, string> = {
  Start: "Starting",
  Stop: "Stopping",
  Restart: "Restarting",
  Pause: "Pausing",
  Resume: "Resuming",
  "Reset progress": "Resetting",
};

export interface ActivityVerbRowProps {
  readonly added: AddedActivityRow;
  /** Off the pinned manifest. A `resident` activity draws no verb at all: adding and removing are
   * its only verbs. Omit, or pass `episodic`, for the ordinary six-verb set. */
  readonly lifecycle?: string | undefined;
  readonly size?: "sm" | "md";
}

/** One added activity's lifecycle verbs, from its run state and lifecycle, in one layout. */
export function ActivityVerbRow({ added, lifecycle, size }: ActivityVerbRowProps) {
  const verb = useActivityVerb();
  const status = runStatusOf(added.mount);
  const verbs = lifecycle === "resident" ? [] : verbsFor(status);
  const pendingVerb = verb.isPending ? verb.variables?.verb : undefined;

  if (verbs.length === 0) return null;

  return (
    <Toolbar>
      {verbs.map((word) => (
        <Button
          key={word}
          size={size ?? "sm"}
          variant={word === "Start" || word === "Resume" ? "primary" : "secondary"}
          loading={pendingVerb === word}
          disabled={verb.isPending && pendingVerb !== word}
          onClick={() => {
            verb.mutate({ row: added, verb: word });
          }}
        >
          {pendingVerb === word ? RECEIPT[word] : word}
        </Button>
      ))}
      {verb.isError ? <StatusBadge tone="danger">{verb.error.message}</StatusBadge> : null}
    </Toolbar>
  );
}
